// DE profile › Configuration › Permissions (D10 scope editor; Parity P6a Permissions tab, design
// §8.1): what this agent does on its own (Direct), what it asks first (Ask) and what it never does
// (Never), per action class, each with where that answer comes from (the class default, this
// agent's setting, an older seat setting, the company ceiling). Managers change a class with the
// row's own control; the answers shown while editing come from the server's preview of the edit
// (`GET …/agent-policy/preview?modes=`), the same rule enforcement runs. Saving sends the
// version read (`If-Match`): if someone else saved in between, nothing is overwritten and the page
// says so. "Default" removes this agent's setting, so a class nobody set is exactly the default.
//
// Below the classes: what agents may never do (no setting changes it), board fences, the keys'
// scopes (read-only, issued with the agent), and "Can {name} …?", the explainer: one action, on
// an optional board, answered with the reason chain.

import type { OperationOutput } from "@companyos/sdk";
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  NativeSelect,
  NativeSelectOption,
  Notice,
  SaveDock,
  ToggleGroup,
  ToggleGroupItem,
  workerName,
} from "@companyos/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BanIcon, KeyRoundIcon, SearchCheckIcon, ShieldIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { boardsQuery } from "../chat/chat-queries";
import type { CompanyAccess } from "../company";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "./de-status";

type Policy = OperationOutput<"seats.agentPolicy">;
type Row = Policy["classes"][number];
type Cls = Row["class"];
type SeatMode = Row["seat_mode"];
type Verdict = Row["verdict"];
type Source = Row["source"];
type Explain = OperationOutput<"seats.agentPolicyExplain">;

/** Plain names for the classes (the operations line under each says what it covers). */
export const CLASS_LABELS: Record<Cls, string> = {
  read: "Read",
  comment: "Comment and message",
  task_create: "Create tasks",
  assign: "Assign people",
  field_set: "Edit task fields",
  upload: "Upload files",
  status_own: "Change status of its own tasks",
  status_other: "Change status of other tasks",
  complete_other: "Complete or reopen other tasks",
  reorder: "Reorder tasks",
  task_move: "Move tasks to another board",
  task_delete: "Delete tasks",
  restore: "Restore tasks",
  file_delete: "Delete files",
  board_config: "Change board setup",
  automation_write: "Change automations",
  playbook: "Run playbooks",
  page_edit: "Edit pages",
  bulk: "Bulk changes (over 10 items)",
  external: "Act outside (email, Slack, webhooks)",
  delegation: "Delegate to other agents",
  // CALENDAR-C1: create, move or cancel calendar events.
  calendar: "Change calendars",
};

const VERDICT_LABEL: Record<Verdict, string> = {
  allow_in_run: "Direct",
  propose: "Ask",
  deny: "Never",
};
const VERDICT_TONE: Record<Verdict, "success" | "warning" | "danger"> = {
  allow_in_run: "success",
  propose: "warning",
  deny: "danger",
};
const MODE_LABEL: Record<SeatMode, string> = {
  inherit: "Default",
  direct: "Direct",
  ask: "Ask",
  never: "Never",
};
const MODES: readonly SeatMode[] = ["inherit", "direct", "ask", "never"];

/** Where an answer comes from, in words. */
export function sourceText(s: Source): string {
  switch (s.level) {
    case "default":
      return "Class default";
    case "seat":
      return "Set for this agent";
    case "legacy":
      return "Older seat setting";
    case "company":
      return "Company ceiling";
    case "board":
      return "Board setting";
    case "never":
      return "Never allowed";
    case "fence":
      return "Board fence";
    case "unknown_class":
      return "Unknown action: asks";
    case "bulk":
      return "Bulk rule (over 10 items)";
    case "template":
      return "Role overlay or probation";
    case "guardrail":
      return "Guardrail: always asks (over 10 items)";
  }
}

const policyKey = (co: string, seatId: string) => ["agent-policy", co, seatId] as const;
const modesParam = (draft: Partial<Record<Cls, SeatMode>>) =>
  Object.entries(draft)
    .map(([c, m]) => `${c}:${m}`)
    .join(",");

function Section({
  icon: Icon,
  title,
  children,
  slot,
}: {
  icon: typeof ShieldIcon;
  title: string;
  children: ReactNode;
  slot: string;
}) {
  return (
    <section className="flex min-w-0 flex-col gap-2" data-slot={slot} aria-label={title}>
      <h2 className="flex items-center gap-2 text-sm font-medium">
        <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
        {title}
      </h2>
      {children}
    </section>
  );
}

export function DePermissionsTab({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const seatId = de.seat?.id ?? "";
  const co = company.id;
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Partial<Record<Cls, SeatMode>>>({});
  // The policy an edit started from: kept while editing, so a background refetch never moves the
  // version a save sends (Codex r1 #2); cleared on save or discard, replaced only on "Load
  // their version".
  const [base, setBase] = useState<Policy | null>(null);
  const [conflict, setConflict] = useState(false);
  const policy = useQuery({
    queryKey: policyKey(co, seatId),
    queryFn: () => api.client.seats.agentPolicy({ params: { co, id: seatId } }),
    enabled: de.seat !== null,
  });
  const current = base ?? policy.data;
  const modes = modesParam(draft);
  const preview = useQuery({
    queryKey: [...policyKey(co, seatId), "preview", current?.version ?? "", modes],
    queryFn: () =>
      api.client.seats.agentPolicyPreview({ params: { co, id: seatId }, query: { modes } }),
    enabled: de.seat !== null && modes !== "",
    placeholderData: keepPreviousData,
  });
  const done = () => {
    setDraft({});
    setBase(null);
    setConflict(false);
  };
  const save = useMutation({
    mutationFn: (p: Policy) =>
      api.client.seats.agentPolicyUpdate({
        params: { co, id: seatId },
        ifMatch: p.version,
        body: { modes: draft as Record<string, SeatMode> },
      }),
    onSuccess: (next) => {
      qc.setQueryData(policyKey(co, seatId), next);
      // previews and answers made against the old policy are stale now (Codex r1 #7)
      void qc.invalidateQueries({ queryKey: policyKey(co, seatId) });
      done();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) setConflict(true);
    },
  });
  const edit = (cls: Cls, m: SeatMode, stored: SeatMode) => {
    if (!base && policy.data) setBase(policy.data);
    setDraft((d) => {
      const { [cls]: _drop, ...rest } = d;
      return m === stored ? rest : { ...rest, [cls]: m };
    });
  };

  if (!de.seat)
    return (
      <p className="text-sm text-muted-foreground">
        Permissions are set per seat, and this agent has no active seat.
      </p>
    );
  if (policy.isPending && !current) return <Loading label="Loading permissions" rows={4} />;
  if (policy.isError && !current) {
    if (policy.error instanceof ApiError && policy.error.status === 403)
      return (
        <p className="text-sm text-muted-foreground">
          Only managers and this agent's supervisor see its permissions.
        </p>
      );
    return <LoadError what="Permissions" error={policy.error} />;
  }
  if (!current) return <Loading label="Loading permissions" rows={4} />;
  const p = current;
  const dirty = modes !== "";
  // A preview answers the draft only when it was made for these very modes: a kept-over result
  // of the previous draft, or a failure, is never shown as the draft's verdict (Codex r3 #3).
  const previewState: "ready" | "pending" | "failed" | "none" = !dirty
    ? "none"
    : preview.isError && !preview.isFetching
      ? "failed"
      : preview.data && !preview.isPlaceholderData && !preview.isFetching
        ? "ready"
        : "pending";
  const previewed = new Map(
    (previewState === "ready" && preview.data ? preview.data.classes : []).map((c) => [c.class, c]),
  );

  return (
    <div className="flex min-w-0 flex-col gap-6" data-slot="de-permissions">
      <p className="max-w-prose text-sm text-muted-foreground">
        What {workerName(de.display_name)} does on its own (Direct), asks you first (Ask) or never
        does (Never).
        {p.editable
          ? " Change a row with its control; Default goes back to the class default."
          : " Managers change these."}
      </p>
      {conflict ? (
        <Notice tone="warning" data-slot="policy-conflict">
          Someone else changed these permissions since you opened them. Nothing was overwritten.{" "}
          <Button
            variant="link"
            size="sm"
            onClick={async () => {
              setConflict(false);
              const r = await policy.refetch();
              // the edit now starts from their version (the draft is kept on top of it)
              setBase(r.data ?? null);
              void qc.invalidateQueries({ queryKey: [...policyKey(co, seatId), "explain"] });
            }}
          >
            Load their version
          </Button>{" "}
          (your changes stay until you discard them).
        </Notice>
      ) : null}
      {save.isError && !conflict ? (
        <Notice tone="danger">{problemText(save.error, "The permissions were not saved.")}</Notice>
      ) : null}

      <Section icon={ShieldIcon} title="Actions" slot="policy-classes">
        <ul className="divide-y divide-border rounded-lg border border-border">
          {p.classes.map((row) => {
            const next = draft[row.class];
            const changed = next !== undefined && next !== row.seat_mode;
            // a changed row shows its preview, or that it has none yet; an unchanged row is
            // decided by its stored setting alone (classes are decided one by one)
            const shown = changed ? previewed.get(row.class) : row;
            return (
              <li
                key={row.class}
                data-class={row.class}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2"
              >
                <div className="min-w-0 flex-1 basis-64">
                  <div className="flex items-center gap-2 text-sm">
                    {CLASS_LABELS[row.class]}
                    {changed ? (
                      <Badge variant="outline" data-slot="changed">
                        changed
                      </Badge>
                    ) : null}
                  </div>
                  <div className="truncate text-xs text-muted-foreground" title={row.operations}>
                    {row.operations}
                    {row.limit ? ` · limit: ${row.limit}` : ""}
                  </div>
                  {row.note && !changed ? (
                    <div className="text-xs text-warning-text" data-slot="practice-note">
                      {row.note}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-2" data-slot="effective">
                  {/* what the agent meets on today's write paths (Codex r2 #2, #3) */}
                  {shown ? (
                    <>
                      <Badge variant={VERDICT_TONE[shown.in_practice]} dot>
                        {VERDICT_LABEL[shown.in_practice]}
                      </Badge>
                      <span className="text-xs text-muted-foreground" data-slot="source">
                        {sourceText(shown.source)}
                        {shown.in_practice !== shown.verdict
                          ? ` (policy: ${VERDICT_LABEL[shown.verdict]})`
                          : ""}
                      </span>
                    </>
                  ) : previewState === "failed" ? (
                    <span className="text-xs text-danger-text" data-slot="preview-failed">
                      Preview failed
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground" data-slot="preview-pending">
                      Previewing…
                    </span>
                  )}
                </div>
                {!row.enforced ? (
                  // not held on every write path yet: shown, not settable (Codex r1 #1)
                  <span className="text-xs text-muted-foreground" data-slot="not-enforced">
                    Not settable yet
                    {row.seat_mode !== "inherit" ? ` (stored: ${MODE_LABEL[row.seat_mode]})` : ""}
                  </span>
                ) : p.editable ? (
                  <ToggleGroup
                    variant="chip"
                    value={[next ?? row.seat_mode]}
                    onValueChange={(v: unknown[]) => {
                      const m = v[0] as SeatMode | undefined;
                      if (m) edit(row.class, m, row.seat_mode);
                    }}
                    aria-label={`${CLASS_LABELS[row.class]}: setting`}
                  >
                    {MODES.filter(
                      (m) => m === "inherit" || (row.settable_modes as string[]).includes(m),
                    ).map((m) => (
                      <ToggleGroupItem key={m} value={m}>
                        {MODE_LABEL[m]}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    Setting: {MODE_LABEL[row.seat_mode]}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
        {p.unknown_rows.length > 0 ? (
          <p className="text-xs text-muted-foreground" data-slot="unknown-rows">
            Stored settings this version does not know (they ask, never allow):{" "}
            {p.unknown_rows.map((r) => `${r.class}: ${r.mode}`).join(", ")}
          </p>
        ) : null}
      </Section>

      <Section icon={BanIcon} title="Never, whatever the setting" slot="policy-never">
        <ul className="flex flex-wrap gap-1.5">
          {p.never.map((n) => (
            <li key={n}>
              <Badge variant="outline">{n}</Badge>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground" data-slot="policy-fences">
          Board fences: none. Boards cannot fence agents yet; when they can, a fence wins over any
          setting here.
        </p>
      </Section>

      <Section icon={KeyRoundIcon} title="Key scopes" slot="policy-keys">
        {p.keys.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No live agent key on this seat: runs started by the runtime are not limited by a key.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {p.keys.map((k) => (
              <li
                key={k.id}
                data-key={k.id}
                className="flex flex-wrap items-center gap-2 px-3 py-2"
              >
                <code className="text-xs">{k.key_prefix}…</code>
                <span className="text-xs text-muted-foreground">issued with the agent:</span>
                {k.scopes.map((s) => (
                  <Badge key={s} variant="outline">
                    {s}
                  </Badge>
                ))}
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground">
          A run started from a key can do only what the key's scopes allow, and then only what the
          settings above allow. Read-only here.
        </p>
      </Section>

      <Explainer
        api={api}
        company={company}
        seatId={seatId}
        name={workerName(de.display_name)}
        version={policy.data?.version ?? p.version}
      />

      {p.editable ? (
        <SaveDock
          dirty={dirty}
          saving={save.isPending}
          message={
            previewState === "failed" ? (
              <span className="text-danger-text">
                The preview failed: {problemText(preview.error, "try again")}.{" "}
                <Button variant="link" size="sm" onClick={() => void preview.refetch()}>
                  Retry
                </Button>
              </span>
            ) : previewState === "pending" ? (
              "Previewing the change…"
            ) : (
              `${Object.keys(draft).length} permission change${Object.keys(draft).length === 1 ? "" : "s"}`
            )
          }
          saveDisabled={previewState !== "ready"}
          onSave={() => {
            if (previewState === "ready") save.mutate(p);
          }}
          onDiscard={done}
        />
      ) : null}
    </div>
  );
}

const STEP_TEXT: Record<Explain["steps"][number]["level"], string> = {
  never: "Hard prohibition",
  fence: "Board fence",
  unknown_class: "Known action",
  default: "Class default",
  legacy: "Older seat setting",
  seat: "This agent's setting",
  company: "Company ceiling",
  board: "Board setting",
  bulk: "Bulk rule",
  template: "Role overlay or probation",
  guardrail: "Guardrail (always asks)",
};

/** "Can {name} …?": one action on an optional board, answered with the reason chain. */
function Explainer({
  api,
  company,
  seatId,
  name,
  version,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  name: string;
  /** The stored policy's version: an answer is about that policy (Codex r1 #7). */
  version: string;
}) {
  const co = company.id;
  const [cls, setCls] = useState<Cls>("task_move");
  const [boardId, setBoardId] = useState("");
  const [asked, setAsked] = useState<{ cls: Cls; boardId: string; at: number } | null>(null);
  const boards = useQuery(boardsQuery(api, co));
  const answer = useQuery({
    // each Check asks again (`at`); a saved change makes a new version, so a new answer
    queryKey: ["agent-policy", co, seatId, "explain", version, asked],
    queryFn: () =>
      api.client.seats.agentPolicyExplain({
        params: { co, id: seatId },
        query: { class: asked?.cls ?? "", ...(asked?.boardId ? { boardIds: asked.boardId } : {}) },
      }),
    enabled: asked !== null,
  });
  const boardName = (id: string) => boards.data?.data.find((b) => b.id === id)?.name ?? "the board";

  return (
    <Section icon={SearchCheckIcon} title={`Can ${name} …?`} slot="policy-explain">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setAsked({ cls, boardId, at: Date.now() });
        }}
      >
        <NativeSelect
          aria-label="Action"
          value={cls}
          onChange={(e) => setCls(e.target.value as Cls)}
        >
          {(Object.keys(CLASS_LABELS) as Cls[]).map((c) => (
            <NativeSelectOption key={c} value={c}>
              {CLASS_LABELS[c]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <NativeSelect
          aria-label="Board"
          value={boardId}
          onChange={(e) => setBoardId(e.target.value)}
        >
          <NativeSelectOption value="">on any board</NativeSelectOption>
          {(boards.data?.data ?? []).map((b) => (
            <NativeSelectOption key={b.id} value={b.id}>
              on {b.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <Button type="submit" variant="secondary" size="sm">
          Check
        </Button>
      </form>
      {asked === null ? null : answer.isPending ? (
        <Loading label="Checking" rows={1} />
      ) : answer.isError ? (
        <LoadError what="The answer" error={answer.error} />
      ) : (
        <div className="flex flex-col gap-2 text-sm" data-slot="explain-answer">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={VERDICT_TONE[answer.data.in_practice]} dot>
              {VERDICT_LABEL[answer.data.in_practice]}
            </Badge>
            <span>
              {answer.data.in_practice === "allow_in_run"
                ? `Yes: ${name} does this on its own`
                : answer.data.in_practice === "propose"
                  ? `Only with approval: ${name} asks first`
                  : `No: ${name} never does this`}
              {asked.boardId ? ` on ${boardName(asked.boardId)}` : ""} (
              {sourceText(answer.data.source)}
              {answer.data.in_practice !== answer.data.verdict
                ? `; policy: ${VERDICT_LABEL[answer.data.verdict]}`
                : ""}
              ).
            </span>
          </div>
          {answer.data.note ? (
            <p className="text-xs text-warning-text" data-slot="explain-note">
              {answer.data.note}
            </p>
          ) : null}
          {answer.data.refusal ? (
            <p className="text-xs text-muted-foreground">{answer.data.refusal.message}</p>
          ) : null}
          {!answer.data.enforced ? (
            <p className="text-xs text-muted-foreground" data-slot="explain-advisory">
              This is the policy's answer; not every way of doing this checks it yet.
            </p>
          ) : null}
          <ol className="flex flex-col gap-1 text-xs" data-slot="explain-steps">
            {answer.data.steps.map((s, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: the chain is ordered and fixed
                key={i}
                data-applied={s.applied || undefined}
                className={s.applied ? "font-medium" : "text-muted-foreground"}
              >
                {i + 1}. {STEP_TEXT[s.level]}
                {s.level === "board" && s.scope_id ? ` (${boardName(s.scope_id)})` : ""}:{" "}
                {s.mode ? MODE_LABEL[s.mode] : "none"}
                {s.applied
                  ? i === answer.data.steps.findLastIndex((x) => x.applied)
                    ? " ← decides"
                    : " ← applied, then tightened"
                  : ""}
              </li>
            ))}
          </ol>
          <p className="text-xs text-muted-foreground" data-slot="explain-work">
            {answer.data.work.varies
              ? "The key scopes this needs depend on the operation (a task, a file, a thread)."
              : answer.data.work.needs.length === 0
                ? "Key scopes do not limit this action."
                : `A run started from a key needs ${answer.data.work.needs
                    .map((n) => `${n.family}:${n.access}`)
                    .join(", ")} first: ${
                    answer.data.work.keys.length === 0
                      ? "no live key on this seat"
                      : answer.data.work.keys
                          .map((k) => `${k.key_prefix}… ${k.allowed ? "has it" : "lacks it"}`)
                          .join(", ")
                  }.`}
          </p>
        </div>
      )}
    </Section>
  );
}
